import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import GroupsIcon from '@mui/icons-material/Groups';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import PhoneIcon from '@mui/icons-material/Phone';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import controlDeckLogo from '../assets/controldeckLogo.png';

const landingColors = {
  ink: '#10212b',
  muted: '#5f6f7a',
  primary: '#0f766e',
  primaryDark: '#134e4a',
  crm: '#00796b',
  margin: '#455a64',
  inventory: '#3949ab',
  reporting: '#c2185b',
  tenant: '#1565c0',
};

const valueCards = [
  {
    title: 'Use sales signals to grow and protect revenue',
    icon: QueryStatsIcon,
    color: landingColors.reporting,
  },
  {
    title: 'Improve your margins making better buy decisions',
    icon: Inventory2Icon,
    color: landingColors.inventory,
  },
  {
    title: 'Monitor article margins before they drift',
    icon: RequestQuoteIcon,
    color: landingColors.margin,
  },
  {
    title: 'Know the right next action for every customer',
    icon: GroupsIcon,
    color: landingColors.crm,
  },
];

const platformPillars = [
  { label: 'Fortnox foundation', icon: BusinessIcon },
  { label: 'Multitenant companies', icon: BusinessIcon },
  { label: 'Role-aware users', icon: ManageAccountsIcon },
];

const previewRows = [
  { customer: 'Nordic Deli Group', status: 'Follow up', sales: '+14%', margin: '31%' },
  { customer: 'Market Hall Stockholm', status: 'Ready report', sales: '+8%', margin: '27%' },
  { customer: 'City Wholesale', status: 'Purchase watch', sales: '-3%', margin: '22%' },
];

const customerSalesTrendRows = [
  {
    number: '102',
    name: 'North Market Deli AB',
    averageWeeklySales: '4,686.50',
    lastWeekSales: '4,473',
    difference: 'Down < 10%',
    direction: 'downSoft',
  },
  {
    number: '103',
    name: 'Harbor Kitchen Group',
    averageWeeklySales: '8,699',
    lastWeekSales: '9,034',
    difference: 'Up < 10%',
    direction: 'upSoft',
  },
  {
    number: '110',
    name: 'Central Bakery Partners',
    averageWeeklySales: '571.75',
    lastWeekSales: '0',
    difference: 'Down > 30%',
    direction: 'downStrong',
  },
  {
    number: '128',
    name: 'Green Table Stockholm AB',
    averageWeeklySales: '927.50',
    lastWeekSales: '2,000',
    difference: 'Up > 30%',
    direction: 'upStrong',
  },
  {
    number: '132',
    name: 'Urban Dining Collective',
    averageWeeklySales: '1,697.25',
    lastWeekSales: '0',
    difference: 'Down > 30%',
    direction: 'downStrong',
  },
  {
    number: '135',
    name: 'Rosendal Food Hall AB',
    averageWeeklySales: '5,316',
    lastWeekSales: '0',
    difference: 'Down > 30%',
    direction: 'downStrong',
  },
  {
    number: '137',
    name: 'Metro Bistro Group AB',
    averageWeeklySales: '3,612.75',
    lastWeekSales: '5,878',
    difference: 'Up > 30%',
    direction: 'upStrong',
  },
];

const customerSalesTrendColumns = [
  { key: 'name', label: 'Customer Name', width: '260px' },
  { key: 'averageWeeklySales', label: 'Avg Weekly Sales', width: '190px' },
  { key: 'lastWeekSales', label: 'Last Week Sales', width: '190px' },
  { key: 'difference', label: 'Sales trend', width: '190px' },
];

const crmTimelineRows = [
  {
    date: 'SUN, 21 JUN 2026',
    type: 'Visit',
    title: 'Nina',
    loggedBy: 'operationsUser',
    time: '21 Jun 2026, 18:47',
    color: '#f57c00',
    icon: StorefrontIcon,
  },
  {
    date: 'SAT, 20 JUN 2026',
    type: 'Call',
    title: 'Call - unassigned',
    loggedBy: 'operationsUser',
    time: '20 Jun 2026, 18:47',
    color: '#1976d2',
    icon: PhoneIcon,
  },
];

const purchaseDecisionRows = [
  {
    article: '10000',
    description: 'Fresh mozzarella block - 500g x 8',
    supplier: 'North Dairy Cooperative',
    averageQuantity: '24',
    lastWeek: '0',
    inStock: '0 KG',
    toOrder: true,
  },
  {
    article: '10002',
    description: 'Julienne cheese blend - 3kg',
    supplier: 'Alpine Foods AB',
    averageQuantity: '237',
    lastWeek: '189',
    inStock: '503 KG',
    toOrder: false,
  },
  {
    article: '10003',
    description: 'Creamy cheese loaf - 1kg / 16kg case',
    supplier: 'Alpine Foods AB',
    averageQuantity: '27.75',
    lastWeek: '1',
    inStock: '532 KG',
    toOrder: false,
  },
  {
    article: '10004',
    description: 'Diced cheese cubes - 3kg',
    supplier: 'Alpine Foods AB',
    averageQuantity: '936.75',
    lastWeek: '870',
    inStock: '864 KG',
    toOrder: true,
  },
];

const purchaseDecisionColumns = [
  { key: 'description', label: 'Article', width: '200px' },
  { key: 'supplier', label: 'Supplier', width: '165px' },
  { key: 'averageQuantity', label: 'Avg Qty', width: '95px' },
  { key: 'lastWeek', label: 'Last week', width: '110px' },
  { key: 'inStock', label: 'In Stock', width: '105px' },
  { key: 'toOrder', label: 'Action', width: '105px' },
];

const marginInsightRows = [
  {
    article: '10000',
    name: 'Fresh mozzarella block - 500g x 8',
    supplier: 'North Dairy Cooperative',
    purchasePrice: '82',
    salesPrice: '115',
    marginNoFreight: '40.2%',
    margin: '29.9%',
    rank: '-',
    highlighted: false,
  },
  {
    article: '10001',
    name: 'Julienne cheese blend - 3kg',
    supplier: '-',
    purchasePrice: '52',
    salesPrice: '116',
    marginNoFreight: '123.1%',
    margin: '106.6%',
    rank: '-',
    highlighted: false,
  },
  {
    article: '10002',
    name: 'Napoli cut cheese blend - 3kg',
    supplier: 'Alpine Foods AB',
    purchasePrice: '60',
    salesPrice: '116',
    marginNoFreight: '93.3%',
    margin: '79.0%',
    rank: '5',
    highlighted: true,
  },
  {
    article: '10003',
    name: 'Creamy cheese loaf - 1kg / 16kg case',
    supplier: 'Alpine Foods AB',
    purchasePrice: '60',
    salesPrice: '116',
    marginNoFreight: '93.3%',
    margin: '79.0%',
    rank: '-',
    highlighted: false,
  },
  {
    article: '10004',
    name: 'Diced cheese cubes - 3kg',
    supplier: 'Alpine Foods AB',
    purchasePrice: '60',
    salesPrice: '116',
    marginNoFreight: '93.3%',
    margin: '79.0%',
    rank: '1',
    highlighted: true,
  },
];

const marginInsightColumns = [
  { key: 'name', label: 'Article Name', width: '260px' },
  { key: 'supplier', label: 'Supplier Name', width: '250px' },
  { key: 'purchasePrice', label: 'Purchase Price', width: '180px' },
  { key: 'salesPrice', label: 'Sales Price', width: '170px' },
  { key: 'marginNoFreight', label: 'Margin', width: '220px' },
  { key: 'margin', label: 'Margin', width: '160px' },
  { key: 'rank', label: 'Rank', width: '120px' },
];

const showcaseSectionSx = {
  borderRadius: 4,
  p: { xs: 2, md: 3 },
  backgroundColor: '#f7f9fb',
  border: '1px solid rgba(16,33,43,0.09)',
  boxShadow: '0 18px 44px rgba(16,33,43,0.07)',
};

function FeatureCta() {
  return (
    <Button
      variant="contained"
      component={RouterLink}
      to="/request-info"
      sx={{
        alignSelf: { xs: 'center', sm: 'flex-start' },
        backgroundColor: landingColors.primary,
        borderRadius: 2,
        fontWeight: 800,
        fontSize: { xs: '0.82rem', sm: '0.875rem' },
        minHeight: { xs: 36, sm: 40 },
        px: { xs: 1.75, sm: 2.5 },
        py: { xs: 0.6, sm: 1 },
        boxShadow: '0 12px 24px rgba(15,118,110,0.18)',
        '&:hover': { backgroundColor: landingColors.primaryDark },
      }}
    >
      Ask for info
    </Button>
  );
}

function SectionEyebrow({ Icon, color, label }) {
  return (
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ alignSelf: 'flex-start' }}>
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          width: 56,
          height: 56,
          borderRadius: 2,
          backgroundColor: `${color}14`,
          color,
          border: `1px solid ${color}2e`,
          boxShadow: `0 14px 28px ${color}18`,
          alignItems: 'center',
          justifyContent: 'center',
          flex: '0 0 auto',
        }}
      >
        <Icon sx={{ fontSize: 32 }} />
      </Box>
      <Chip
        label={label}
        sx={{
          alignSelf: 'center',
          backgroundColor: `${color}14`,
          color,
          fontWeight: 800,
        }}
      />
    </Stack>
  );
}

export function LandingPage() {
  return <PublicLandingFrame topBar={<TopLandingBar />} />;
}

export default function LoginPage({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [company, setCompany] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (event) => {
    event.preventDefault();

    const trimmedUsername = username.trim();
    const trimmedPassword = password.trim();
    const trimmedCompany = company.trim();

    setError('');

    if (!trimmedUsername) {
      setError('Username cannot be empty.');
      return;
    }

    if (!trimmedPassword) {
      setError('Password cannot be empty.');
      return;
    }

    if (!trimmedCompany) {
      setError('Please enter your company.');
      return;
    }

    setIsSubmitting(true);

    try {
      await onLogin({
        username: trimmedUsername,
        password: trimmedPassword,
        company: trimmedCompany,
      });
      navigate('/', { replace: true });
    } catch (loginError) {
      console.error('Login error:', loginError);
      setError(loginError.message || 'Could not connect to server. Please try again later.');
      setIsSubmitting(false);
    }
  };

  return (
    <CenteredLoginPage
      company={company}
      error={error}
      handleLogin={handleLogin}
      isSubmitting={isSubmitting}
      password={password}
      setCompany={setCompany}
      setPassword={setPassword}
      setShowPassword={setShowPassword}
      setUsername={setUsername}
      showPassword={showPassword}
      username={username}
    />
  );
}

function PublicLandingFrame({ topBar }) {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: '#ffffff',
        color: landingColors.ink,
        overflowX: 'hidden',
      }}
    >
      <Container
        maxWidth="xl"
        disableGutters
        sx={{
          backgroundColor: '#ffffff',
          px: { xs: 2, md: 3 },
          pt: { xs: 1, md: 0.5 },
          pb: { xs: 3, md: 0.5 },
        }}
      >
        <Stack spacing={{ xs: 2, md: 1 }}>
          {topBar}

          <Box
            component="section"
            sx={{
              ...showcaseSectionSx,
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 7fr) minmax(0, 5fr)' },
              gap: { xs: 3, lg: 4 },
              alignItems: 'stretch',
              minWidth: 0,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Stack spacing={3.25} sx={{ height: '100%', justifyContent: 'center' }}>
                <Stack spacing={2.25}>
                  <Typography
                    component="h1"
                    sx={{
                      maxWidth: 820,
                      fontSize: { xs: '2.15rem', sm: '3.1rem', md: '4.2rem' },
                      lineHeight: 0.98,
                      fontWeight: 900,
                      letterSpacing: 0,
                      overflowWrap: 'anywhere',
                    }}
                  >
                    Sell smarter, buy better, and protect revenues and margins.
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      maxWidth: 720,
                      color: landingColors.muted,
                      lineHeight: 1.55,
                      fontWeight: 500,
                    }}
                  >
                    ControlDeck connects light-touch CRM, customer sales statistics, inventory
                    accuracy, purchasing insight, and financial control in one secure workspace.
                    It operates on top of Fortnox to give your company a clearer view of the business
                    fundamentals that drive sales, stock, purchasing, and margin.
                  </Typography>
                </Stack>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  alignItems={{ xs: 'center', sm: 'flex-start' }}
                >
                  <Button
                    variant="contained"
                    component={RouterLink}
                    to="/request-info"
                    sx={{
                      backgroundColor: landingColors.primary,
                      borderRadius: 2,
                      fontWeight: 800,
                      fontSize: { xs: '0.82rem', sm: '0.9375rem' },
                      minHeight: { xs: 36, sm: 46 },
                      px: { xs: 1.75, sm: 3 },
                      py: { xs: 0.6, sm: 1.25 },
                      boxShadow: '0 16px 30px rgba(15,118,110,0.25)',
                      width: 'auto',
                      '&:hover': { backgroundColor: landingColors.primaryDark },
                    }}
                  >
                    Ask for info
                  </Button>
                </Stack>
              </Stack>
            </Box>

            <Box sx={{ display: { xs: 'none', md: 'block' }, minWidth: 0 }}>
              <Stack spacing={2} sx={{ height: '100%' }}>
                <ProductPreview />
              </Stack>
            </Box>
          </Box>

          <Box
            id="value-drivers"
            sx={{
              display: { xs: 'none', md: 'grid' },
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr)',
                md: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(4, minmax(0, 1fr))',
              },
              gap: 2,
              minWidth: 0,
              pb: { xs: 3, md: 5 },
              mb: { xs: 2, md: 4 },
              borderBottom: '1px solid rgba(16,33,43,0.1)',
            }}
          >
            {valueCards.map((card) => {
              const Icon = card.icon;

              return (
                <Box key={card.title} sx={{ minWidth: 0 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      position: 'relative',
                      overflow: 'hidden',
                      height: '100%',
                      borderRadius: 2,
                      p: { xs: 2.25, md: 2.5 },
                      background: `linear-gradient(145deg, ${card.color}12 0%, rgba(255,255,255,0.96) 44%, #ffffff 100%)`,
                      border: `1px solid ${card.color}2b`,
                      boxShadow: '0 20px 46px rgba(16,33,43,0.1)',
                      transition:
                        'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        inset: '0 0 auto 0',
                        height: 4,
                        backgroundColor: card.color,
                      },
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        borderColor: `${card.color}66`,
                        boxShadow: '0 26px 56px rgba(16,33,43,0.16)',
                      },
                    }}
                  >
                    <Stack spacing={2} sx={{ height: '100%', justifyContent: 'space-between' }}>
                      <Box
                        sx={{
                          width: { xs: 56, md: 62 },
                          height: { xs: 56, md: 62 },
                          borderRadius: 2,
                          backgroundColor: '#ffffff',
                          color: card.color,
                          boxShadow: `0 16px 30px ${card.color}24`,
                          border: `1px solid ${card.color}30`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Icon sx={{ fontSize: { xs: 31, md: 34 } }} />
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          color: landingColors.ink,
                          fontSize: { xs: '1.28rem', md: '1.42rem' },
                          fontWeight: 900,
                          lineHeight: 1.18,
                        }}
                      >
                        {card.title}
                      </Typography>
                    </Stack>
                  </Paper>
                </Box>
              );
            })}
          </Box>

          <CustomerSalesTrendShowcase />
          <PurchaseDecisionShowcase />
          <MarginInsightShowcase />
          <CrmCustomerShowcase />
        </Stack>
      </Container>
    </Box>
  );
}

function TopLandingBar() {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        p: { xs: 1, md: 1.75 },
        backgroundColor: '#ffffff',
        maxWidth: '100%',
      }}
    >
      <Stack
        direction="row"
        spacing={{ xs: 1, sm: 1.5 }}
        alignItems="center"
        justifyContent="space-between"
        sx={{ minWidth: 0 }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          justifyContent="flex-start"
          sx={{ minWidth: 0 }}
        >
          <Box
            component="img"
            src={controlDeckLogo}
            alt="ControlDeck"
            sx={{
              width: { xs: 190, sm: 320, md: 420 },
              maxWidth: { xs: '62vw', sm: 360, md: 430 },
              height: { xs: 58, sm: 92, md: 112 },
              objectFit: 'contain',
              objectPosition: 'left center',
              borderRadius: 1.5,
              flex: '0 0 auto',
            }}
          />
        </Stack>

        <Button
          variant="contained"
          component={RouterLink}
          to="/login"
          size="large"
          sx={{
            minHeight: { xs: 34, sm: 44 },
            backgroundColor: landingColors.primary,
            borderRadius: { xs: 1.5, sm: 2 },
            fontWeight: 800,
            fontSize: { xs: '0.78rem', sm: '0.9375rem' },
            px: { xs: 1.5, sm: 3 },
            whiteSpace: 'nowrap',
            width: 'auto',
            flex: '0 0 auto',
            '&:hover': { backgroundColor: landingColors.primaryDark },
          }}
        >
          Sign in
        </Button>
      </Stack>
    </Paper>
  );
}

function CenteredLoginPage({
  company,
  error,
  handleLogin,
  isSubmitting,
  password,
  setCompany,
  setPassword,
  setShowPassword,
  setUsername,
  showPassword,
  username,
}) {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#ffffff',
        color: landingColors.ink,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: { xs: 4, md: 6 },
      }}
    >
      <Paper
        id="signin"
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 440,
          borderRadius: 3,
          p: { xs: 2.5, sm: 3.5 },
          backgroundColor: '#ffffff',
          border: '1px solid rgba(16,33,43,0.1)',
          boxShadow: '0 24px 58px rgba(16,33,43,0.12)',
        }}
      >
        <Stack spacing={2.5} alignItems="stretch">
          <Box
            component="img"
            src={controlDeckLogo}
            alt="ControlDeck"
            sx={{
              width: '100%',
              maxWidth: 330,
              height: { xs: 96, sm: 104 },
              objectFit: 'contain',
              objectPosition: 'center',
              borderRadius: 1.5,
              alignSelf: 'center',
            }}
          />

          <Stack
            component="form"
            spacing={1.5}
            onSubmit={handleLogin}
            sx={{
              alignItems: 'stretch',
              width: '100%',
            }}
          >
            <TextField
              label="Username"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              autoComplete="username"
              size="small"
              autoFocus
              fullWidth
            />

            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              size="small"
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((show) => !show)}
                      edge="end"
                      aria-label="Toggle password visibility"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Company"
              value={company}
              onChange={(event) => setCompany(event.target.value)}
              autoComplete="organization"
              placeholder="Company"
              size="small"
              fullWidth
            />

            <Button
              variant="contained"
              type="submit"
              loading={isSubmitting}
              sx={{
                minHeight: 44,
                backgroundColor: landingColors.primary,
                borderRadius: 2,
                fontWeight: 800,
                mt: 0.5,
                whiteSpace: 'nowrap',
                '&:hover': { backgroundColor: landingColors.primaryDark },
              }}
            >
              Sign in
            </Button>
          </Stack>

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </Paper>
    </Box>
  );
}

function ProductPreview() {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        p: { xs: 2, md: 2.5 },
        backgroundColor: '#10212b',
        color: '#fff',
        minHeight: 360,
        boxShadow: '0 26px 60px rgba(16,33,43,0.24)',
        overflow: 'hidden',
        maxWidth: '100%',
      }}
    >
      <Stack spacing={2.25}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1.25}
        >
          <Box>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.62)' }}>
              Fortnox-connected workspace
            </Typography>
            <Typography variant="h6" fontWeight={850}>
              Customer, stock, and margin control
            </Typography>
          </Box>
          <Chip
            label="3 tenants"
            size="small"
            sx={{ backgroundColor: 'rgba(255,255,255,0.12)', color: '#fff', fontWeight: 700 }}
          />
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(3, minmax(0, 1fr))' },
            gap: 1.25,
            minWidth: 0,
          }}
        >
          {platformPillars.map((pillar) => {
            const Icon = pillar.icon;

            return (
              <Box key={pillar.label} sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    borderRadius: 2,
                    p: 1.5,
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.1)',
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Icon fontSize="small" sx={{ color: '#67e8f9' }} />
                    <Typography variant="caption" fontWeight={750}>
                      {pillar.label}
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            );
          })}
        </Box>

        <Box
          sx={{
            borderRadius: 2,
            backgroundColor: '#f8fafc',
            color: landingColors.ink,
            overflow: 'hidden',
          }}
        >
          <Stack spacing={0}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              sx={{ px: 1.5, py: 1.25, borderBottom: '1px solid #e5e7eb' }}
            >
              <Typography variant="body2" fontWeight={850}>
                Account priorities
              </Typography>
              <Chip label="CRM + sales stats" size="small" color="success" variant="outlined" />
            </Stack>

            {previewRows.map((row) => (
              <Stack
                key={row.customer}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.25}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                sx={{ px: 1.5, py: 1.2, borderBottom: '1px solid #edf2f7' }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" fontWeight={800}>
                    {row.customer}
                  </Typography>
                  <Typography variant="caption" sx={{ color: landingColors.muted }}>
                    {row.status}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                  <Box sx={{ flex: { xs: 1, sm: '0 0 74px' } }}>
                    <Typography variant="caption" sx={{ color: landingColors.muted }}>
                      Sales
                    </Typography>
                    <Typography variant="body2" fontWeight={850}>
                      {row.sales}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: { xs: 1, sm: '0 0 74px' } }}>
                    <Typography variant="caption" sx={{ color: landingColors.muted }}>
                      Margin
                    </Typography>
                    <Typography variant="body2" fontWeight={850}>
                      {row.margin}
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            ))}
          </Stack>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
            gap: 1.25,
            minWidth: 0,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ borderRadius: 2, p: 1.5, backgroundColor: 'rgba(20,184,166,0.16)' }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.72)' }}>
                Purchase signal
              </Typography>
              <Typography variant="body2" fontWeight={850}>
                Inventory trend supports reorder
              </Typography>
              <LinearProgress
                variant="determinate"
                value={78}
                sx={{ mt: 1, height: 7, borderRadius: 99, backgroundColor: 'rgba(255,255,255,0.16)' }}
              />
            </Box>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ borderRadius: 2, p: 1.5, backgroundColor: 'rgba(99,102,241,0.18)' }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.72)' }}>
                Customer report
              </Typography>
              <Typography variant="body2" fontWeight={850}>
                Sales matrix ready for review
              </Typography>
              <LinearProgress
                variant="determinate"
                value={92}
                sx={{ mt: 1, height: 7, borderRadius: 99, backgroundColor: 'rgba(255,255,255,0.16)' }}
              />
            </Box>
          </Box>
        </Box>
      </Stack>
    </Paper>
  );
}

function getDifferencePillSx(direction) {
  const variants = {
    upSoft: {
      backgroundColor: '#edf7ed',
      borderColor: '#dbe9db',
      color: '#477246',
    },
    upStrong: {
      backgroundColor: '#dfeadb',
      borderColor: '#d0dec9',
      color: '#3f6b3f',
    },
    downSoft: {
      backgroundColor: '#fff6df',
      borderColor: '#f0dfb8',
      color: '#9a762f',
    },
    downStrong: {
      backgroundColor: '#fbe8e8',
      borderColor: '#efd7d7',
      color: '#d84a43',
    },
  };

  return variants[direction] || variants.upSoft;
}

function PurchaseDecisionShowcase() {
  return (
    <Box
      component="section"
      sx={{
        ...showcaseSectionSx,
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 0.8fr) minmax(0, 1.7fr)' },
        gap: { xs: 2.5, lg: 4 },
        alignItems: 'center',
        minWidth: 0,
      }}
    >
      <Stack spacing={1.75} sx={{ minWidth: 0 }}>
        <SectionEyebrow
          Icon={Inventory2Icon}
          color={landingColors.inventory}
          label="Purchase decisions"
        />
        <Typography
          component="h2"
          sx={{
            fontSize: { xs: '1.9rem', md: '2.8rem' },
            lineHeight: 1.05,
            fontWeight: 900,
            letterSpacing: 0,
          }}
        >
          Improve your margins making better buy decisions
        </Typography>
        <Typography sx={{ color: landingColors.muted, lineHeight: 1.7, maxWidth: 520 }}>
          ControlDeck combines recent sales, average demand, current stock, and item signals so
          purchase quantities can be decided with less manual checking.
        </Typography>
        <FeatureCta />
      </Stack>

      <Paper
        elevation={0}
        sx={{
          display: { xs: 'none', md: 'block' },
          borderRadius: 3,
          backgroundColor: 'rgba(255,255,255,0.92)',
          border: '1px solid rgba(16,33,43,0.08)',
          boxShadow: '0 24px 58px rgba(16,33,43,0.12)',
          overflow: 'hidden',
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            px: { xs: 1.5, md: 2 },
            py: 1.5,
            borderBottom: '1px solid rgba(16,33,43,0.08)',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={850}>
              Purchase Order Suggestions
            </Typography>
            <Typography variant="caption" sx={{ color: landingColors.muted }}>
              Sample data with anonymized products and suppliers
            </Typography>
          </Box>
          <Chip label="Stock aware" size="small" color="primary" variant="outlined" />
        </Box>

        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: { xs: 780, lg: 0 } }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: purchaseDecisionColumns.map((column) => column.width).join(' '),
                borderBottom: '1px solid #e1e5ea',
                backgroundColor: '#fff',
              }}
            >
              {purchaseDecisionColumns.map((column) => (
                <Typography
                  key={column.key}
                  fontWeight={850}
                  sx={{ px: 2, py: 1.5, color: '#272d33', whiteSpace: 'nowrap' }}
                >
                  {column.label}
                </Typography>
              ))}
            </Box>

            {purchaseDecisionRows.map((row) => (
              <Box
                key={row.description}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: purchaseDecisionColumns.map((column) => column.width).join(' '),
                  alignItems: 'center',
                  borderBottom: '1px solid #e1e5ea',
                  backgroundColor: '#fff',
                  minHeight: 72,
                }}
              >
                <Typography sx={{ px: 2, color: '#3c434a', lineHeight: 1.35 }}>{row.description}</Typography>
                <Typography sx={{ px: 2, color: '#3c434a', lineHeight: 1.35 }}>{row.supplier}</Typography>
                <Typography sx={{ px: 2, color: '#3c434a', textAlign: 'right' }}>
                  {row.averageQuantity}
                </Typography>
                <Typography sx={{ px: 2, color: '#3c434a', textAlign: 'right' }}>{row.lastWeek}</Typography>
                <Typography sx={{ px: 2, color: '#3c434a', textAlign: 'right' }}>{row.inStock}</Typography>
                <Box sx={{ px: 2 }}>
                  {row.toOrder ? (
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        justifyContent: 'center',
                        minWidth: 86,
                        px: 1.5,
                        py: 0.45,
                        borderRadius: 99,
                        border: '1px solid #f0caca',
                        backgroundColor: '#fdeceb',
                        color: '#d64b43',
                        fontWeight: 850,
                        fontSize: '0.86rem',
                      }}
                    >
                      Buy
                    </Box>
                  ) : (
                    <Typography sx={{ color: '#3c434a', textAlign: 'center' }}>-</Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

function MarginInsightShowcase() {
  return (
    <Box
      component="section"
      sx={{
        ...showcaseSectionSx,
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1.7fr) minmax(0, 0.8fr)' },
        gap: { xs: 2.5, lg: 4 },
        alignItems: 'center',
        minWidth: 0,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          display: { xs: 'none', md: 'block' },
          borderRadius: 3,
          backgroundColor: 'rgba(255,255,255,0.92)',
          border: '1px solid rgba(16,33,43,0.08)',
          boxShadow: '0 24px 58px rgba(16,33,43,0.12)',
          overflow: 'hidden',
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            px: { xs: 1.5, md: 2 },
            py: 1.5,
            borderBottom: '1px solid rgba(16,33,43,0.08)',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={850}>
              Article Margin Ranking
            </Typography>
            <Typography variant="caption" sx={{ color: landingColors.muted }}>
              Sample data with anonymized products and suppliers
            </Typography>
          </Box>
          <Chip label="Margin view" size="small" color="success" variant="outlined" />
        </Box>

        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: 1360 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: marginInsightColumns.map((column) => column.width).join(' '),
                borderBottom: '1px solid #e1e5ea',
                backgroundColor: '#fff',
              }}
            >
              {marginInsightColumns.map((column) => (
                <Typography
                  key={column.key}
                  fontWeight={850}
                  sx={{ px: 2, py: 1.5, color: '#272d33', whiteSpace: 'nowrap' }}
                >
                  {column.label}
                </Typography>
              ))}
            </Box>

            {marginInsightRows.map((row) => (
              <Box
                key={row.name}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: marginInsightColumns.map((column) => column.width).join(' '),
                  alignItems: 'center',
                  borderBottom: '1px solid #e1e5ea',
                  backgroundColor: row.highlighted ? '#edf7ed' : '#fff',
                  minHeight: 72,
                }}
              >
                <Typography sx={{ px: 2, color: '#3c434a', lineHeight: 1.35 }}>{row.name}</Typography>
                <Typography sx={{ px: 2, color: '#3c434a', lineHeight: 1.35 }}>{row.supplier}</Typography>
                <Typography sx={{ px: 2, color: '#3c434a' }}>{row.purchasePrice}</Typography>
                <Typography sx={{ px: 2, color: '#3c434a' }}>{row.salesPrice}</Typography>
                <Typography sx={{ px: 2, color: '#3c434a' }}>{row.marginNoFreight}</Typography>
                <Typography sx={{ px: 2, color: '#3c434a' }}>{row.margin}</Typography>
                <Typography sx={{ px: 2, color: '#3c434a' }}>{row.rank}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Paper>

      <Stack spacing={1.75} sx={{ minWidth: 0 }}>
        <SectionEyebrow
          Icon={RequestQuoteIcon}
          color={landingColors.margin}
          label="Margin intelligence"
        />
        <Typography
          component="h2"
          sx={{
            fontSize: { xs: '1.9rem', md: '2.8rem' },
            lineHeight: 1.05,
            fontWeight: 900,
            letterSpacing: 0,
          }}
        >
          Monitor article margins before they drift
        </Typography>
        <Typography sx={{ color: landingColors.muted, lineHeight: 1.7, maxWidth: 520 }}>
          ControlDeck makes article margins easy to review across your assortment, so you can spot
          margin pressure early and keep profitability under control.
        </Typography>
        <FeatureCta />
      </Stack>
    </Box>
  );
}

function CustomerSalesTrendShowcase() {
  return (
    <Box
      component="section"
      sx={{
        ...showcaseSectionSx,
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1.65fr) minmax(0, 0.85fr)' },
        gap: { xs: 2.5, lg: 4 },
        alignItems: 'center',
        minWidth: 0,
      }}
    >
      <Stack spacing={1.75} sx={{ minWidth: 0, order: { xs: 1, lg: 2 } }}>
        <SectionEyebrow
          Icon={QueryStatsIcon}
          color={landingColors.reporting}
          label="Customer sales trends"
        />
        <Typography
          component="h2"
          sx={{
            fontSize: { xs: '1.9rem', md: '2.8rem' },
            lineHeight: 1.05,
            fontWeight: 900,
            letterSpacing: 0,
          }}
        >
          Use sales signals to grow and protect revenue
        </Typography>
        <Typography sx={{ color: landingColors.muted, lineHeight: 1.7, maxWidth: 520 }}>
          ControlDeck helps you see which customers are growing, slowing down, or at risk, then
          focus sales activity where it can protect revenue or create the next opportunity.
        </Typography>
        <FeatureCta />
      </Stack>

      <Paper
        elevation={0}
        sx={{
          display: { xs: 'none', md: 'block' },
          order: { xs: 2, lg: 1 },
          borderRadius: 3,
          backgroundColor: 'rgba(255,255,255,0.9)',
          border: '1px solid rgba(16,33,43,0.08)',
          boxShadow: '0 24px 58px rgba(16,33,43,0.12)',
          overflow: 'hidden',
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            px: { xs: 1.5, md: 2 },
            py: 1.5,
            borderBottom: '1px solid rgba(16,33,43,0.08)',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={850}>
              Customer Sales Trend
            </Typography>
            <Typography variant="caption" sx={{ color: landingColors.muted }}>
              Sample data with anonymized customers
            </Typography>
          </Box>
          <Chip label="Weekly view" size="small" color="primary" variant="outlined" />
        </Box>

        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: 830 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: customerSalesTrendColumns.map((column) => column.width).join(' '),
                borderBottom: '1px solid #e1e5ea',
                backgroundColor: '#fff',
              }}
            >
              {customerSalesTrendColumns.map((column) => (
                <Typography
                  key={column.key}
                  fontWeight={850}
                  sx={{ px: 2, py: 1.5, color: '#272d33', whiteSpace: 'nowrap' }}
                >
                  {column.label}
                </Typography>
              ))}
            </Box>

            {customerSalesTrendRows.map((row) => {
              const pillSx = getDifferencePillSx(row.direction);

              return (
                <Box
                  key={row.name}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: customerSalesTrendColumns.map((column) => column.width).join(' '),
                    alignItems: 'center',
                    borderBottom: '1px solid #e1e5ea',
                    backgroundColor: '#fff',
                    minHeight: 64,
                  }}
                >
                  <Typography sx={{ px: 2, color: '#3c434a' }}>{row.name}</Typography>
                  <Typography sx={{ px: 2, color: '#3c434a' }}>{row.averageWeeklySales}</Typography>
                  <Typography sx={{ px: 2, color: '#3c434a' }}>{row.lastWeekSales}</Typography>
                  <Box sx={{ px: 2 }}>
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        justifyContent: 'center',
                        minWidth: 130,
                        px: 1.5,
                        py: 0.45,
                        borderRadius: 99,
                        border: '1px solid',
                        fontWeight: 850,
                        fontSize: '0.86rem',
                        ...pillSx,
                      }}
                    >
                      {row.difference}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

function CrmCustomerShowcase() {
  return (
    <Box
      component="section"
      sx={{
        ...showcaseSectionSx,
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 0.8fr) minmax(0, 1.55fr)' },
        gap: { xs: 2.5, lg: 4 },
        alignItems: 'center',
        minWidth: 0,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          display: { xs: 'none', md: 'block' },
          order: { xs: 2, lg: 2 },
          borderRadius: 3,
          backgroundColor: 'rgba(255,255,255,0.92)',
          border: '1px solid rgba(16,33,43,0.08)',
          boxShadow: '0 24px 58px rgba(16,33,43,0.12)',
          p: { xs: 1.75, md: 2.5 },
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <Stack spacing={2}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 0.9fr) minmax(0, 1.1fr)' },
              gap: 2,
              alignItems: 'stretch',
            }}
          >
            <Paper elevation={0} sx={{ borderRadius: 2, p: 1.75, border: '1px solid rgba(16,33,43,0.08)' }}>
              <Typography variant="h6" fontWeight={900}>
                Open activities
              </Typography>
              <Typography variant="body2" sx={{ color: landingColors.muted }}>
                Planned CRM work stays visible until it is resolved.
              </Typography>

              <Paper
                elevation={0}
                sx={{ mt: 1.5, borderRadius: 2, p: 1.5, border: '1px solid rgba(16,33,43,0.08)' }}
              >
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
                    <Box>
                      <Typography fontWeight={900}>Complaint</Typography>
                      <Typography sx={{ color: landingColors.muted }}>Due 17 Jun 2026</Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <Chip label="Issue" size="small" color="error" variant="outlined" />
                      <Chip label="Team North" size="small" color="success" variant="outlined" />
                    </Stack>
                  </Stack>
                  <Typography variant="body2" sx={{ color: landingColors.muted }}>
                    Updated by operationsUser on 20 Jun 2026, 18:48
                  </Typography>
                </Stack>
              </Paper>
            </Paper>

            <Paper elevation={0} sx={{ borderRadius: 2, p: 1.75, border: '1px solid rgba(16,33,43,0.08)' }}>
              <Typography variant="h6" fontWeight={900}>
                Timeline
              </Typography>
              <Typography variant="body2" sx={{ color: landingColors.muted }}>
                Completed customer history, newest first.
              </Typography>

              <Box sx={{ mt: 1.5, pl: { xs: 0, md: 2 }, borderLeft: { xs: 'none', md: '3px solid #e5f2f1' } }}>
                <Stack spacing={1.5}>
                  {crmTimelineRows.map((entry) => {
                    const Icon = entry.icon;

                    return (
                      <Box key={`${entry.date}-${entry.type}`} sx={{ minWidth: 0 }}>
                        <Chip
                          label={entry.date}
                          size="small"
                          sx={{
                            mb: 1,
                            backgroundColor: `${landingColors.crm}10`,
                            color: landingColors.crm,
                            fontWeight: 850,
                          }}
                        />
                        <Paper
                          elevation={0}
                          sx={{
                            borderRadius: 2,
                            p: 1.5,
                            border: '1px solid rgba(16,33,43,0.08)',
                            position: 'relative',
                          }}
                        >
                          <Stack direction="row" spacing={1.5} alignItems="flex-start">
                            <Box
                              sx={{
                                width: 34,
                                height: 34,
                                borderRadius: '50%',
                                backgroundColor: `${entry.color}14`,
                                color: entry.color,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flex: '0 0 auto',
                              }}
                            >
                              <Icon fontSize="small" />
                            </Box>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                justifyContent="space-between"
                                spacing={1}
                              >
                                <Chip
                                  label={entry.type}
                                  size="small"
                                  sx={{
                                    alignSelf: 'flex-start',
                                    backgroundColor: `${entry.color}12`,
                                    color: entry.color,
                                    fontWeight: 850,
                                  }}
                                />
                                <Typography variant="body2" sx={{ color: landingColors.muted }}>
                                  {entry.time}
                                </Typography>
                              </Stack>
                              <Typography sx={{ mt: 1, fontWeight: 900 }}>{entry.title}</Typography>
                              <Typography variant="body2" sx={{ mt: 1, color: landingColors.muted }}>
                                Logged by {entry.loggedBy}
                              </Typography>
                            </Box>
                          </Stack>
                        </Paper>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            </Paper>
          </Box>
        </Stack>
      </Paper>

      <Stack spacing={1.75} sx={{ minWidth: 0, order: { xs: 1, lg: 1 } }}>
        <SectionEyebrow
          Icon={GroupsIcon}
          color={landingColors.crm}
          label="Light-touch CRM"
        />
        <Typography
          component="h2"
          sx={{
            fontSize: { xs: '1.9rem', md: '2.8rem' },
            lineHeight: 1.05,
            fontWeight: 900,
            letterSpacing: 0,
          }}
        >
          Know the right next action for every customer
        </Typography>
        <Typography sx={{ color: landingColors.muted, lineHeight: 1.7, maxWidth: 520 }}>
          ControlDeck keeps the customer's key context, open work, contacts, and activity history
          in one place, helping your team focus follow-up where it matters most.
        </Typography>
        <FeatureCta />
      </Stack>
    </Box>
  );
}
